import { Box, Card, CardContent, Divider, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography, useTheme } from "@mui/material";
import { BAR_CHART_PLUGINS, BarChartPluginSignatures, BarPlot } from "@mui/x-charts/BarChart";
import { ChartsDataProvider } from "@mui/x-charts/ChartsDataProvider";
import { ChartsGrid } from "@mui/x-charts/ChartsGrid";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import { ChartsSurface } from "@mui/x-charts/ChartsSurface";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { Fragment } from "react";
import { BonusMonth, BonusOverview as BonusOverviewData } from "../../../types/BonusOverview";
import styles from "./bonusOverviewStyles";
import ratingStar from "../../../assets/icon/bonus/ratingStar.svg";
import performanceBonusMedal from "../../../assets/icon/bonus/performanceBonusMedal.svg";

const caseNumber = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const integerNumber = new Intl.NumberFormat("de-DE");
const monthName = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" });
const shortMonthName = new Intl.DateTimeFormat("de-DE", { month: "short" });
const dateForMonth = (month: string) => new Date(`${month}-01T12:00:00`);
const money = (amount: number, currency: "EUR" | "€") =>
  `${new Intl.NumberFormat("de-DE", { minimumFractionDigits: Number.isInteger(amount) ? 0 : 2, maximumFractionDigits: 2 }).format(amount)} ${currency}`;
const rating = (value: number | null) => value === null ? "—" : caseNumber.format(value);

type BonusChartProps = { history: BonusMonth[]; currentMonth: BonusOverviewData["currentMonth"]; target: number };

function BonusChart({ history, currentMonth, target }: BonusChartProps) {
  const theme = useTheme();
  const months = currentMonth === null ? history : [...history, currentMonth];
  const currentIndex = currentMonth === null ? -1 : history.length;
  // Fourteen bands keep the 4rem history columns; the current month spans two bands.
  const chartValues = Array.from({ length: 14 }, (_, index) => months[index]?.weightedCases ?? null);
  const hasSpace = months.length < 13;

  return (
    <Card sx={styles.chartCard}>
      <Typography variant="h2" component="h2" sx={styles.chartHeading}>Abgeschlossene Fälle (gewichtet)</Typography>
      <TableContainer>
        <Box sx={styles.chartContent}>
          <Box sx={styles.chart}>
            <Grid container wrap="nowrap" sx={styles.chartColumns}>
              <Grid sx={styles.axis} />
              {months.map((month) => {
                const isCurrent = month === currentMonth;

                return (
                  <Grid key={month.month} data-month={month.month} sx={[styles.monthColumn, isCurrent && styles.currentMonthColumn]}>
                    <Typography variant="body2" sx={styles.month}>{shortMonthName.format(dateForMonth(month.month)).replace(".", "")}</Typography>
                    <Typography variant="caption" sx={styles.year}>{month.month.slice(0, 4)}</Typography>
                    {isCurrent && <Typography variant="caption" sx={styles.running}>laufend</Typography>}
                  </Grid>
                );
              })}
            </Grid>
            <ChartsDataProvider<"bar", BarChartPluginSignatures>
              width={1072}
              height={280}
              plugins={BAR_CHART_PLUGINS}
              margin={{ top: 28, bottom: 64, left: 0, right: 0 }}
              xAxis={[{ scaleType: "band", data: chartValues.map((_, index) => index), position: "none", categoryGapRatio: 0.6 }]}
              yAxis={[{
                min: 0,
                max: Math.max(1, target, ...months.map((month) => month.weightedCases)) * 1.2,
                width: 176,
                tickNumber: 5,
                disableLine: true,
                disableTicks: true,
                valueFormatter: (value: number) => value === target ? "" : integerNumber.format(value),
              }]}
              series={[{
                type: "bar",
                data: chartValues,
                color: theme.palette.grey[400],
                barLabel: ({ value }) => value === null ? null : caseNumber.format(value),
                barLabelPlacement: "outside",
              }]}
              skipAnimation
            >
              <ChartsSurface sx={styles.barChart}>
                <ChartsGrid horizontal />
                <ChartsReferenceLine
                  y={target}
                  label={`Performance-Bonus  ${integerNumber.format(target)}`}
                  labelAlign="start"
                  spacing={{ x: -16, y: 0 }}
                  labelStyle={{ textAnchor: "end", dominantBaseline: "central" }}
                />
                <BarPlot
                  slotProps={{
                    bar: ({ dataIndex }) => ({
                      "data-bonus-bar": dataIndex === currentIndex ? "current" : "past",
                      style: { fill: dataIndex === currentIndex && currentMonth !== null && currentMonth.bonusCases > 0 ? theme.palette.primary.main : theme.palette.grey[400] },
                    }),
                    barLabel: ({ dataIndex }) => ({
                      className: dataIndex === currentIndex ? "bonusCurrentBarLabel" : "",
                      dominantBaseline: "auto",
                      dy: chartValues[dataIndex] === 0 ? -8 : 0,
                      style: { stroke: theme.palette.background.paper },
                    }),
                  }}
                />
                <ChartsYAxis />
              </ChartsSurface>
            </ChartsDataProvider>
          </Box>
          <Table size="small" sx={styles.table}>
            <colgroup>
              <col style={{ width: "11rem" }} />
              {months.map((month) => <col key={month.month} style={{ width: month === currentMonth ? "8rem" : "4rem" }} />)}
              {hasSpace && <col />}
            </colgroup>
            <TableBody>
              <TableRow sx={styles.tableRow}>
                <TableCell component="th" sx={styles.tableLabel}>kumuliert unter<br />Performance-Bonus</TableCell>
                {months.map((month) => <TableCell key={month.month} sx={styles.tableCell}>{caseNumber.format(month.balanceAfter)}</TableCell>)}
                {hasSpace && <TableCell sx={styles.tableCell} />}
              </TableRow>
              <TableRow sx={[styles.tableRow, styles.amountRow]}>
                <TableCell component="th" sx={styles.tableLabel}>Bonus</TableCell>
                {months.map((month) => <TableCell key={month.month} sx={[styles.tableCell, { color: "primary.main" }]}>{money(month.totalAmount, "€")}</TableCell>)}
                {hasSpace && <TableCell sx={styles.tableCell} />}
              </TableRow>
              <TableRow sx={styles.tableRow}>
                <TableCell component="th" sx={styles.tableLabel}>Bewertung</TableCell>
                {months.map((month) => <TableCell key={month.month} sx={styles.tableCell}>{rating(month.rating)}{month.rating !== null && " ★"}</TableCell>)}
                {hasSpace && <TableCell sx={styles.tableCell} />}
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </TableContainer>
    </Card>
  );
}

type BonusOverviewProps = { overview: BonusOverviewData };

export default function BonusOverview({ overview }: BonusOverviewProps) {
  const current = overview.currentMonth;

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card sx={[styles.card, styles.progress, current?.performanceBonusReached === true && styles.success]}>
            <CardContent sx={styles.cardContent}>
              {current?.performanceBonusReached === true ? (
                <Stack sx={styles.successContent}>
                  <img src={performanceBonusMedal} alt="" width={112} height={64} />
                  <Typography variant="h2" color="primary" component="h2">Performance-Bonus<br />erreicht</Typography>
                </Stack>
              ) : (
                <Stack spacing={0.625}>
                  <Typography variant="subtitle1">Noch</Typography>
                  <Typography variant="h1" color="primary">{current === null ? "—" : caseNumber.format(current.remainingCases)}</Typography>
                  <Typography variant="subtitle1">Fälle bis zum<br />Performance-Bonus</Typography>
                  <Box>
                    <Grid container sx={[styles.row, { height: "1.25rem" }]}><Typography variant="body2" color="text.secondary">aus diesem Monat:</Typography><Typography variant="body2">{current === null ? "—" : caseNumber.format(current.remainingThisMonth)}</Typography></Grid>
                    <Grid container sx={[styles.row, { height: "1.25rem" }]}><Typography variant="body2" color="text.secondary">aus Vormonaten:</Typography><Typography variant="body2">{current === null ? "—" : caseNumber.format(current.remainingPreviousMonths)}</Typography></Grid>
                  </Box>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card sx={styles.card}>
            <CardContent sx={styles.cardContent}>
              <Stack spacing={1}>
                <Typography variant="h5">Bonus aktueller Monat</Typography>
                <Typography variant="h3" color="primary">{money(current?.totalAmount ?? 0, "EUR")}</Typography>
                <Typography variant="caption" color="text.secondary">{current !== null && `${monthName.format(dateForMonth(current.month))} · laufend`}</Typography>
                <Divider />
                <Typography variant="h5">Ausgezahlt gesamt</Typography>
                <Typography variant="h3">{money(overview.paidTotal, "EUR")}</Typography>
                <Typography variant="caption" color="text.secondary">seit {new Date(`${overview.bonusStartAt}T12:00:00`).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card sx={styles.card}>
            <CardContent sx={styles.cardContent}>
              <Stack spacing={1}>
                <Typography variant="h5">Aktive Fälle</Typography>
                <Typography variant="h1">{integerNumber.format(overview.activeCases.total)}</Typography>
                <Box sx={styles.categories}>
                  {overview.activeCases.categories.map(({ label, count }) => (
                    <Grid key={label} container sx={[styles.row, { minHeight: "1.625rem" }]}><Typography variant="body2">{label}</Typography><Typography variant="body2">{integerNumber.format(count)} Fälle</Typography></Grid>
                  ))}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card sx={styles.card}>
            <CardContent sx={styles.cardContent}>
              <Stack spacing={1}>
                {[
                  { label: "Persönliche Bewertung", value: overview.personalRating, count: overview.personalRatingCount },
                  { label: "Team Bewertung", value: overview.teamRating, count: overview.teamRatingCount },
                ].map(({ label, value, count }, index) => (
                  <Fragment key={label}>
                    {index > 0 && <Divider />}
                    <Stack spacing={0.5} sx={styles.ratingGroup}>
                      <Typography variant="h5" component="div">{label}</Typography>
                      <Typography variant="caption" color="text.secondary">letzte 12 Monate</Typography>
                      <Grid container sx={styles.rating}>
                        <img src={ratingStar} alt="" width={22} height={22} />
                        <Typography variant="h3">
                          {rating(value)}{" "}
                          <Typography variant="caption" component="span">({integerNumber.format(count)})</Typography>
                        </Typography>
                      </Grid>
                    </Stack>
                  </Fragment>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <BonusChart history={overview.history} currentMonth={current} target={overview.monthlyCaseTarget} />
    </>
  );
}
